/**
 * Farmly Markdown v1 ↔ TipTap HTML conversion utilities.
 *
 * Supported subset: bold (**), italic (_), bullet lists (- ), ordered lists (1. ),
 * paragraphs (blank lines), line breaks.
 *
 * These converters allow the TipTap visual editor to load/save Farmly Markdown v1.
 * SoT: docs/architecture/content-formatting.md
 */

// ─── Markdown → HTML (for loading into TipTap) ────────────────────────

/**
 * Convert Farmly Markdown v1 string to simple HTML that TipTap can consume.
 */
export function farmlyMarkdownToHtml(md: string): string {
  if (!md || !md.trim()) return '';

  const lines = md.split('\n');
  const htmlParts: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line → skip (paragraph separator)
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Bullet list block
    if (/^\s*[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(inlineMarkdownToHtml(lines[i].replace(/^\s*[-*]\s/, '')));
        i++;
      }
      htmlParts.push(`<ul>${items.map(t => `<li><p>${t}</p></li>`).join('')}</ul>`);
      continue;
    }

    // Ordered list block
    if (/^\s*\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
        items.push(inlineMarkdownToHtml(lines[i].replace(/^\s*\d+\.\s/, '')));
        i++;
      }
      htmlParts.push(`<ol>${items.map(t => `<li><p>${t}</p></li>`).join('')}</ol>`);
      continue;
    }

    // Regular paragraph — collect consecutive non-blank, non-list lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^\s*[-*]\s/.test(lines[i]) &&
      !/^\s*\d+\.\s/.test(lines[i])
    ) {
      paraLines.push(inlineMarkdownToHtml(lines[i]));
      i++;
    }
    if (paraLines.length > 0) {
      htmlParts.push(`<p>${paraLines.join('<br>')}</p>`);
    }
  }

  return htmlParts.join('');
}

/** Convert inline markdown (bold, italic) to HTML. */
function inlineMarkdownToHtml(text: string): string {
  // Bold: **text**
  let result = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic: _text_ (but not inside words like some_var)
  result = result.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');
  return result;
}

// ─── HTML → Markdown (for saving from TipTap) ─────────────────────────

/**
 * Convert TipTap HTML output to Farmly Markdown v1 string.
 */
export function htmlToFarmlyMarkdown(html: string): string {
  if (!html || html === '<p></p>') return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  if (!root) return '';

  const blocks: string[] = [];

  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const t = child.textContent?.trim();
      if (t) blocks.push(t);
      continue;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const el = child as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'p') {
      blocks.push(nodeToInlineMarkdown(el));
    } else if (tag === 'ul') {
      const items = Array.from(el.querySelectorAll(':scope > li'));
      for (const li of items) {
        blocks.push(`- ${nodeToInlineMarkdown(li as HTMLElement)}`);
      }
    } else if (tag === 'ol') {
      const items = Array.from(el.querySelectorAll(':scope > li'));
      items.forEach((li, idx) => {
        blocks.push(`${idx + 1}. ${nodeToInlineMarkdown(li as HTMLElement)}`);
      });
    } else {
      // Fallback: extract text
      const t = el.textContent?.trim();
      if (t) blocks.push(t);
    }
  }

  return blocks.join('\n\n').trim();
}

/** Convert an element's inline content to markdown (bold, italic, line breaks). */
function nodeToInlineMarkdown(el: HTMLElement): string {
  let result = '';

  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent ?? '';
      continue;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const child = node as HTMLElement;
    const tag = child.tagName.toLowerCase();

    if (tag === 'strong' || tag === 'b') {
      result += `**${nodeToInlineMarkdown(child)}**`;
    } else if (tag === 'em' || tag === 'i') {
      result += `_${nodeToInlineMarkdown(child)}_`;
    } else if (tag === 'br') {
      result += '\n';
    } else if (tag === 'p') {
      // TipTap sometimes wraps li content in <p>
      result += nodeToInlineMarkdown(child);
    } else {
      // Fallback: just get text
      result += child.textContent ?? '';
    }
  }

  return result;
}

/**
 * Count the plain-text length of a markdown string (excluding markup).
 */
export function farmlyMarkdownTextLength(md: string): number {
  return md
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(?<!\w)_(.+?)_(?!\w)/g, '$1')
    .replace(/^\s*[-*]\s/gm, '')
    .replace(/^\s*\d+\.\s/gm, '')
    .length;
}

// ─── Render-time normalisation ────────────────────────────────────────

/**
 * Turn literal escape sequences (a backslash followed by "n", or "\r\n")
 * into real line breaks.
 *
 * Some stored texts contain the two characters `\` + `n` instead of a
 * newline (double-escaped on the way in). Rendered as-is they show up as
 * "\n\n" in the public text and break paragraph and _italic_ parsing.
 */
export function unescapeLiteralNewlines(text: string): string {
  if (!text || !text.includes('\\')) return text;
  return text.replace(/\\r\\n|\\n/g, '\n');
}
